<template>
    <div class="timing-chart">
        <template v-if="pageError">
            <p>
                {{ $t("section-timing-data-error") }}
            </p>
        </template>
        <template v-else>
            <div class="timing-chart__title-group">
                <h4 class="timing-chart__title">{{ title }}</h4>
                <p class="timing-chart__subtitle">{{ subtitle }}</p>
            </div>

            <div
                :id="id"
                class="momentum-chart life-cycle-chart"></div>
        </template>
    </div>
</template>

<script>
// @ is an alias to /src

import Vue from "vue";

import { pad } from "@/utils/helpers/string.js";

import d3 from "@/utils/d3/d3.js";

import { v4 as uuidv4 } from "uuid";

import dayjs from "dayjs";
import weekYear from "dayjs/plugin/weekYear";

var isoWeek = require("dayjs/plugin/isoWeek");
dayjs.extend(isoWeek);

export default {
    name: "TimingEvolutionChart",
    props: {
        type: {
            type: String,
            required: true
        },
        category: {
            type: String,
            required: true
        },
        data: {
            type: Array,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        subtitle: {
            type: String,
            required: true
        },
        activePointTitle: {
            type: String,
            required: false,
            default: "evolution"
        },
        currentWeek: {
            type: String, // Format YYYY-MM-DD
            required: true
        },
        periodType: {
            type: String,
            required: false,
            default: "week"
        }
    },
    data() {
        return {
            id: "evolution-chart-" + uuidv4(),
            isXSmall: true,
            isSmall: false,
            isMobile: true,
            tooltipMobileTitle: null,
            tooltipMobileSubtitle: null,
            pageError: false,
        };
    },
    computed: {
        screenWidth() {
            return this.$store.getters["responsiveModule/getCurrentWindowWidth"];
        },
        artworkFlowerUrl() {
            return require("@/assets/images/artworks/flower.svg");
        },
        // Current data filtered by currentType && category
        currentData() {
            const filteredData = this.data.filter((item) => {
                return item.type === this.type && item.category === this.category;
            });

            return filteredData;
        },
        currentYear() {
            return String(dayjs(this.currentWeek).year());
        },
        currentYearData() {
            if (this.currentData) {
                const items = this.currentData.filter((item) => {
                    return item.x.substring(0, 4) === this.currentYear;
                });

                return items;
            }
        },
        selectedData() {
            let error = false;

            if (this.periodType === "week") {
                if (this.currentYearData) {
                    const currentWeekNumber = dayjs(this.currentWeek).isoWeek();

                    let selectedWeeksNumber;

                    selectedWeeksNumber = [
                        pad(currentWeekNumber - 1, 2),
                        pad(currentWeekNumber, 2),
                        pad(currentWeekNumber + 1, 2),
                    ];

                    const selectedWeeks = selectedWeeksNumber.map((item) => {
                        const currentYearWeek = `${this.currentYear}${item}`;
                        const currentWeekIndex = this.getObjetIndexByKey(this.currentYearData, "x", currentYearWeek);

                        const currentYearData = this.currentYearData[currentWeekIndex];

                        if (currentYearData) {
                            return currentYearData;
                        } else {
                            error = true;
                        }
                    });

                    if (error) {
                        return undefined;
                    } else {
                        return selectedWeeks;
                    }
                }
            } else {

                const currentYear = dayjs(this.currentWeek).year();

                let selectedYearsNumber = [
                    String(currentYear - 1),
                    String(currentYear),
                    String(currentYear + 1),
                ];

                const selectedYear = selectedYearsNumber.map((item) => {
                    // const currentYearWeek = `${this.currentYear}${item}`;

                    const currentYearIndex = this.getObjetIndexByKey(this.currentData, "x", item);
                    const currentYearData = this.currentData[currentYearIndex];

                    if (currentYearData) {
                        return currentYearData;
                    } else {
                        error = true;
                    }
                });

                if (error) {
                    return undefined;
                } else {
                    return selectedYear;
                }
            }
        },
        cleanedData() {
            if (this.selectedData) {
                const cleanedData = this.selectedData.map((item) => {

                    if (this.periodType === "year") {
                        return {
                            "x": d3.timeParse("%Y")(item.x),
                            "y": (item.y) ? item.y : 0,
                            "diff": item.signalyear.toUpperCase()
                        };
                    }

                    return {
                        "x": {
                            "start": d3.timeParse("%Y%m%d")(item.x_first),
                            "end": d3.timeParse("%Y%m%d")(item.x_last),
                        },
                        "y": (item.y) ? item.y : 0,
                        "diff": item.diff
                    };

                });

                return cleanedData;
            }
        },
        titleType() {
            if (this.selectedData && this.selectedData[1]) {
                let titleType = this.selectedData[1].signalweek;

                if (this.periodType === "year") {
                    titleType = this.selectedData[1].signalyear;
                }

                return titleType;
            }
        }
    },
    watch: {
        screenWidth(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.resetLifecycleChart();
                this.buildLifecycleChart();
            }
        },
        selectedData(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.$emit("update-title", this.titleType);
                this.resetLifecycleChart();

                if (!newVal) {
                    this.$emit("error", true);
                    this.pageError = true;
                } else {
                    this.$emit("error", false);
                    this.pageError = false;
                    this.buildLifecycleChart();
                }

            }
        }
    },
    mounted: function() {
        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("x-small", () => {
            this.isXSmall = true;
        }, () => {
            this.isXSmall = false;
        });

        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("small", () => {
            this.isSmall = true;
        }, () => {
            this.isSmall = false;
        });

        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("from-medium", () => {
            this.isMobile = false;
        }, () => {
            this.isMobile = true;
        });

        this.buildLifecycleChart();
        this.$emit("update-title", this.titleType);
    },
    methods: {
        resetLifecycleChart() {
            const wrapper = document.getElementById(this.id);

            wrapper.innerHTML = "";
        },
        buildLifecycleChart() {
            Vue.nextTick(() => {
                /**
                 * [getPointEvolution Return the evolution of a given point based on the previous one]
                 * @param  {object} point [The desired point]
                 * @return {object}       [The evolution (value, sign) object of the given point]
                 */
                const getPointEvolution = (point) => {

                    const diff = point.diff;

                    let sign = "";

                    switch (Math.sign(diff)) {
                    case -1:
                        sign = "-";
                        break;
                    case 1:
                        sign = "+";
                        break;
                    default:
                        "";
                    }

                    const evolution = {
                        "value": String(Math.abs(diff)),
                        "sign": sign
                    };

                    return evolution;
                };


                const yValueRange = d3.extent(this.cleanedData, function(d) {
                    return d.y;
                });
                const adaptedYValueRange = [yValueRange[0], yValueRange[1] + 0.1];

                // set the dimensions and margins of the graph
                var margin = {top: 50, right: 30, bottom: 100, left: 30};

                let width = document.documentElement.clientWidth;
                if (width > 330) {
                    width = 330 + margin.right + margin.left;
                }
                // var width = 330 + margin.right + margin.left;
                var height = 460;

                // append the svg object to the body of the page
                var svg = d3.select("#" + this.id)
                    .append("svg")
                    .attr("class", "timing-chart__svg")
                    .attr("preserveAspectRatio", "xMinYMin meet")
                    .attr("width", width)
                    .attr("height", height + margin.top + 100)
                    .attr("viewBox", `0 0 ${width + 75 + 10} ${height + margin.top + 100}`)
                    .append("g")
                    .attr("transform", "translate(" + (margin.left + margin.right) + ", " + 100 + ")");

                let x;

                if (this.periodType === "year") {
                    x = d3.scaleLinear()
                        .domain([this.cleanedData[0].x, this.cleanedData[this.cleanedData.length - 1].x])
                        .range([0, width - margin.left - margin.right]);
                } else {
                    x = d3.scaleLinear()
                        .domain([this.cleanedData[0].x.start, this.cleanedData[this.cleanedData.length - 1].x.start])
                        .range([0, width - margin.left - margin.right]);
                }


                const xAxisValue = this.cleanedData.map((item) => {
                    if (this.periodType === "year") {
                        return item.x;
                    }

                    return item.x.start;
                });

                const thickValue = this.cleanedData.map((item) => {
                    if (this.periodType === "year") {
                        return item.x;
                    }

                    return item.x.start;
                });

                var xAxis = g => g
                    .attr("transform", `translate(0, ${height + 80})`)
                    .attr("class", "x-axis")
                    .call(d3.axisBottom(x)
                        .tickValues(thickValue)
                        .tickFormat((d) => {
                            // console.log("d", d);
                            let dateOutput;

                            if (this.periodType === "year") {
                                dateOutput = d3.timeFormat("%Y")(d);
                            } else {
                                const startDay = d3.timeFormat("%d")(d);
                                const endDay = d3.timeFormat("%d")(dayjs(d).add(1, "week"));
                                const weekMonth = d3.timeFormat("%m")(d);
                                const weekYear = d3.timeFormat("%Y")(d);

                                dateOutput = `${startDay}-${endDay}/${weekMonth} ${weekYear}`;
                            }

                            return dateOutput;
                        })
                    )
                    .call(g => g.select(".domain").remove())
                    .call(g => g.selectAll(".tick line").clone()
                        .attr("y2", height - margin.top)
                        .attr("transform", `translate(0, -${height + 50})`)
                        .attr("stroke-opacity", 0.05)
                        .attr("stroke-width", 125)
                        .attr("stroke-linecap", "round")
                    );

                // Add Y axis
                var y = d3.scaleLinear()
                    .domain(adaptedYValueRange)
                    .range([ height, 0 ]);
                // svg.append("g")
                //     .attr("class", "y-axis")
                //     .call(d3.axisLeft(y));

                // Add the line
                svg.append("path")
                    .datum(this.cleanedData)
                    .attr("fill", "none")
                    .attr("stroke", "#9a6fe3")
                    .attr("stroke-width", 1.5)
                    .attr("stroke-linecap", "round")
                    .attr("stroke-dasharray", "1, 5")
                    .attr("d", d3.line().curve(d3.curveMonotoneX) // Curve the line
                        .x((d) => {

                            if (this.periodType === "year") {
                                return x(d.x);
                            } else {
                                return x(d.x.start);
                            }

                        })
                        .y(function(d) {
                            return y(d.y);
                        })
                    );

                /* Define the data for the circles */
                var point = svg.append("g")
                    .selectAll("dot")
                    .data(this.cleanedData);



                /*Create and place the "blocks" containing the circle and the text */
                var pointGroup = point.enter()
                    .append("g")
                    .attr("class", (d) => {

                        let classValue = "point-wrapper";


                        if (this.periodType === "year") {
                            if (dayjs(dayjs(this.currentWeek).year()).isSame(dayjs(d.x).year())) {
                                classValue += " point-wrapper--active point-wrapper--active--year";
                            }
                        } else {
                            if (dayjs(dayjs(this.currentWeek).isoWeek()).isSame(dayjs(d.x.start).isoWeek())) {
                                classValue += " point-wrapper--active";
                            }
                        }


                        return classValue;
                    })
                    .attr("transform", (d) => {
                        if (this.periodType === "year") {
                            return "translate("+ x(d.x) + ", " + y(d.y) + ")";
                        } else {
                            return "translate("+ x(d.x.start) + ", " + y(d.y) + ")";
                        }
                    });

                const activePoint = pointGroup.filter((d) => {
                    if (this.periodType === "year") {
                        if (dayjs(dayjs(this.currentWeek).year()).isSame(dayjs(d.x).year())) {
                            return d;
                        }
                    } else {
                        if (dayjs(dayjs(this.currentWeek).isoWeek()).isSame(dayjs(d.x.start).isoWeek())) {
                            return d;
                        }
                    }
                });


                // Add the flower background on active point
                activePoint.append("image")
                    .attr("x", "-91")
                    .attr("y", "-95")
                    .attr("href", this.artworkFlowerUrl)
                    .attr("width", "182")
                    .attr("height", "190")
                    .attr("class", "flower");

                /*Create the circle for each block */
                var circle = pointGroup.append("circle")
                    .attr("class", "point")
                    .attr("r", (d) => {
                        let pointRadius = 19;

                        if (this.periodType === "year") {
                            // Radius for active point
                            if (dayjs(dayjs(this.currentWeek).year()).isSame(dayjs(d.x).year())) {
                                pointRadius = 35;
                            }
                        } else {
                            // Radius for active point
                            if (dayjs(dayjs(this.currentWeek).isoWeek()).isSame(dayjs(d.x.start).isoWeek())) {
                                pointRadius = 35;
                            }
                        }

                        return pointRadius;
                    })
                    .attr("stroke", "#9a6fe3")
                    .attr("stroke-width", 0)
                    .attr("fill", "#9a6fe3");

                /* Create the text for each block */
                pointGroup.append("text")
                    .text((d, i) => {
                        let textContent = (Number.isInteger(d.y)) ? d.y : d.y.toFixed(1);

                        if (this.activePointTitle === "evolution") {
                            // Text value for activePoint

                            if (this.periodType === "year") {
                                if (dayjs(dayjs(this.currentWeek).year()).isSame(dayjs(d.x).year())) {
                                    textContent = d.diff;
                                }
                            } else {
                                if (dayjs(dayjs(this.currentWeek).isoWeek()).isSame(dayjs(d.x.start).isoWeek())) {
                                    const evolution = getPointEvolution(d);

                                    textContent = evolution.sign + evolution.value;
                                }
                            }
                        } else {
                            textContent = this.activePointTitle;
                        }

                        return textContent;
                    })
                    .attr("fill", "#fff")
                    .attr("text-anchor", "middle")
                    .attr("dominant-baseline", "middle");

                svg.append("g")
                    .call(xAxis);
            });
        },
        updateCurrentGroup() {
        },
        toggleHelpModal() {

        },
        handleMouseUp() {
            this.isMouseDown = false;
        },
        handleMouseDown() {
            this.isMouseDown = true;
            this.y = event.pageY;
            this.x = event.pageX;
            this.top = this.$el.scrollTop;
            this.left = this.$el.scrollLeft;
        },
        handleMouseMove(event) {
            this.isDragEnable = true;
            if (this.isDragEnable && this.isMouseDown) {
                this.$el.scrollLeft = this.left - event.pageX + this.x;
                this.$el.scrollTop = this.top - event.pageY + this.y;
            }
        },
        handleMouseLeave() {
            this.isDragEnable = false;
        }
    }
};
</script>

